import { useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { posthog, isPostHogReady } from "../services/posthog"
import { useRedirectIfAuthenticated } from "../hooks/useRedirectIfAuthenticated"

const CALENDLY_URL = "https://calendly.com/nathan-mccarley-landsentry/30min"
const LINKEDIN_URL = "https://www.linkedin.com/in/nathan-mccarley-a47270253/"
const EMAIL = "nathan.mccarley@landsentry.com"
const SIGNUP_GATE_PATH = "/signup"
const SIGNUP_REQUEST_PATH = "/signup?request=1"

const BRAND_GRADIENT = "linear-gradient(135deg, #731DA8 0%, #D5224F 55%, #FF930A 100%)"

const navLinkClass =
  "text-sm font-medium text-neutral-600 transition-colors hover:text-neutral-900"
const linkClass =
  "font-medium text-neutral-900 underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-900"
const cardClass = "rounded-lg border border-neutral-200 bg-white p-8 shadow-sm"
const sectionHeadingClass = "text-lg font-semibold tracking-tight text-neutral-900 md:text-xl"
const sectionLeadClass = "mt-1 text-sm text-neutral-500"
const bodyClass = "text-sm leading-relaxed text-neutral-600 md:text-base md:leading-relaxed"
const proseContainer = "mx-auto w-full max-w-3xl px-6 lg:max-w-4xl"
const wideContainer = "mx-auto w-full max-w-5xl px-6 lg:max-w-6xl"

function trackCta(button) {
  if (isPostHogReady()) {
    posthog.capture("cta_clicked", { button, page: "landing" })
  }
}

function PrimaryButton({ to, children, buttonId }) {
  return (
    <Link
      to={to}
      onClick={() => trackCta(buttonId)}
      className="inline-flex items-center justify-center rounded-md px-4 py-2.5 text-center text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90"
      style={{ background: BRAND_GRADIENT }}
    >
      {children}
    </Link>
  )
}

function OutlineButton({ href, children, buttonId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackCta(buttonId)}
      className="inline-flex items-center justify-center rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-center text-sm font-medium text-neutral-900 shadow-sm transition-colors hover:bg-neutral-50"
    >
      {children}
    </a>
  )
}

function AccessCodeSignupLink({ className = "mt-6", label = "Sign up" }) {
  return (
    <div className={`flex justify-center ${className}`}>
      <div className="rounded-full border border-[#731DA8]/20 bg-[#731DA8]/[0.06] px-5 py-3 text-center shadow-sm md:px-6">
        <p className="text-sm leading-snug text-neutral-700 md:text-base">
          Have an access code?{" "}
          <Link
            to={SIGNUP_GATE_PATH}
            onClick={() => trackCta("signup_with_code")}
            className="font-semibold text-neutral-900 underline decoration-[#731DA8]/40 underline-offset-2 hover:decoration-[#731DA8]"
          >
            {label}
          </Link>
        </p>
      </div>
    </div>
  )
}

const FEATURES = [
  {
    title: "One map, every case type",
    body: "Rezonings, variances, site plans, CUPs, plats, comp plan amendments, annexations. All of them — not just keyword-catchable ones.",
    accent: "#B026FF",
  },
  {
    title: "The full story, on a timeline",
    body: "Every case from first filing to final vote across months. Staff recommendations, conditions, opposition, and board votes extracted.",
    accent: "#00B89C",
  },
  {
    title: "The site plan, ready to open",
    body: "Pull the proposed site plan straight from the case — see what they want to build without digging through the packet.",
    accent: "#FF930A",
  },
]

export default function LandingPage() {
  const location = useLocation()
  useRedirectIfAuthenticated()

  useEffect(() => {
    if (location.pathname !== "/") return
    if (isPostHogReady()) {
      posthog.capture("$pageview", { $current_url: window.location.href, page: "landing" })
    }
  }, [location.pathname])

  return (
    <div className="flex min-h-screen flex-col bg-white text-neutral-900">
      <header className="border-b border-neutral-200">
        <div className={`${wideContainer} flex items-center justify-between py-4`}>
          <Link to="/" className="text-sm font-semibold tracking-tight text-neutral-900">
            LandSentry
          </Link>
          <nav className="flex items-center gap-5">
            <Link to="/support" className={navLinkClass}>
              Support
            </Link>
            <span className="h-4 w-px bg-neutral-200" aria-hidden />
            <div className="flex items-center gap-5">
              <Link to={SIGNUP_GATE_PATH} onClick={() => trackCta("sign_up")} className={navLinkClass}>
                Sign up
              </Link>
              <Link to="/login" onClick={() => trackCta("log_in")} className={navLinkClass}>
                Log in
              </Link>
            </div>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="py-12 md:py-16">
          <div className={`${proseContainer} text-center`}>
            <img
              src="/ls-logo.svg"
              alt=""
              className="mx-auto h-14 w-auto md:h-16 lg:h-[4.5rem]"
              width={280}
              height={70}
              aria-hidden
            />
            <p className="mt-4 text-3xl font-semibold tracking-tight text-neutral-900 md:text-4xl lg:text-5xl">
              LandSentry
              <sup className="text-[0.45em] font-normal align-super">™</sup>
            </p>
            <p className="mt-2 text-sm font-medium tracking-wide text-neutral-500 md:text-base">
              The Entitlements Database
            </p>

            <h1 className="mt-8 text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl lg:text-4xl">
              See every rezoning before your competition does.
            </h1>
            <p className={`mx-auto mt-4 max-w-3xl ${bodyClass}`}>
              LandSentry reads every planning commission document overnight and turns it into one live
              map — every rezoning, variance, site plan, and comprehensive plan amendment across your
              market, cross-referenced and on a timeline.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <PrimaryButton to={SIGNUP_REQUEST_PATH} buttonId="request_access">
                Request Access
              </PrimaryButton>
              <OutlineButton href={CALENDLY_URL} buttonId="book_a_walkthrough">
                Book a Walkthrough
              </OutlineButton>
            </div>
            <AccessCodeSignupLink />
          </div>

          <div className={`${wideContainer} mt-12 md:mt-14`}>
            <img
              src="/images/ProductScreenshot.png"
              alt="LandSentry map with filters and case details for a Charlotte rezoning"
              className="w-full rounded-lg border border-neutral-200 shadow-sm"
              width={1920}
              height={1080}
              loading="eager"
              decoding="async"
            />
          </div>
        </section>

        {/* ROI */}
        <section className="border-t border-neutral-200 py-12">
          <div className={proseContainer}>
            <h2 className={sectionHeadingClass}>What&apos;s a reading-hour worth?</h2>
            <p className={`mt-4 ${bodyClass}`}>
              Right now, staying current means one person pulling up new documents for every county and
              city you track, reading them, building the case list, updating a spreadsheet, dropping
              pins. A single planning docket can run 200 pages. By hand. Every week.
            </p>
            <div className={`relative mt-6 overflow-hidden ${cardClass}`}>
              <div
                className="absolute inset-y-0 left-0 w-1"
                style={{ background: BRAND_GRADIENT }}
                aria-hidden
              />
              <p className={`pl-3 ${bodyClass}`}>
                One analyst spends ~12 hours a month doing manually what LandSentry does overnight.
                That&apos;s{" "}
                <span className="font-medium text-neutral-900">$600/month in analyst time</span> — for
                one county.
              </p>
            </div>
          </div>
        </section>

        {/* Feature panels */}
        <section className="border-t border-neutral-200 py-12">
          <div className={`${wideContainer} grid gap-4 md:grid-cols-3`}>
            {FEATURES.map((feature) => (
              <article
                key={feature.title}
                className={`relative overflow-hidden ${cardClass}`}
              >
                <div
                  className="absolute inset-y-0 left-0 w-1"
                  style={{ backgroundColor: feature.accent }}
                  aria-hidden
                />
                <div className="pl-3">
                  <h3 className={sectionHeadingClass}>{feature.title}</h3>
                  <p className={`mt-3 ${bodyClass}`}>{feature.body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Why not ChatGPT */}
        <section className="border-t border-neutral-200 py-12">
          <div className={proseContainer}>
            <h2 className={sectionHeadingClass}>Why not just use ChatGPT?</h2>
            <p className={`mt-4 ${bodyClass}`}>
              A 200-page document calls the same case &ldquo;Z-27-2025&rdquo; on page 3, &ldquo;the
              Wildwood Road rezoning&rdquo; on page 47, and &ldquo;Item 4&rdquo; on page 156. Next
              month&apos;s meeting says &ldquo;continued from October&rdquo; with no case number at
              all. LandSentry resolves all of it as one case, one timeline — across every document,
              every month, every meeting. That&apos;s the part that took years to build.
            </p>
          </div>
        </section>

        {/* Hero case proof */}
        <section
          className="border-y border-neutral-200 py-10"
          style={{
            background:
              "linear-gradient(135deg, rgba(115,29,168,0.04) 0%, rgba(213,34,79,0.05) 50%, rgba(255,147,10,0.06) 100%)",
          }}
        >
          <div className={`${proseContainer} text-center`}>
            <p className={bodyClass}>
              A single contested rezoning spanned{" "}
              <span className="font-medium text-neutral-900">35 documents and 11 months</span> — eight
              postponements, a committee referral, a recusal, a final 7-0 approval. LandSentry resolved
              all of it into one timeline.
            </p>
          </div>
        </section>

        {/* Coverage + CTA */}
        <section className="py-12 md:py-16">
          <div className={`${proseContainer} text-center`}>
            <p className={bodyClass}>
              <span className="font-medium text-neutral-900">Live now:</span> Charlotte metro —
              Mecklenburg, Union, Gaston counties plus York and Lancaster in SC. Charlotte,
              Huntersville, Davidson, Monroe, and more.
            </p>

            <div className="mx-auto mt-8 max-w-xl md:max-w-2xl">
              <img
                src="/images/CoverageScreenshot.png"
                alt="LandSentry coverage map of the Charlotte metro with planning cases on the map"
                className="w-full rounded-lg border border-neutral-200 shadow-sm"
                width={900}
                height={1000}
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <PrimaryButton to={SIGNUP_REQUEST_PATH} buttonId="request_access">
                Request Access
              </PrimaryButton>
              <OutlineButton href={CALENDLY_URL} buttonId="book_a_walkthrough">
                Book a Walkthrough
              </OutlineButton>
            </div>
            <AccessCodeSignupLink label="Sign up here" />
          </div>
        </section>

        {/* Founder */}
        <section className="border-t border-neutral-200 py-12">
          <div className={`${proseContainer} max-w-lg`}>
            <div className={cardClass}>
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/team/nathan.png"
                  alt="Nathan McCarley"
                  className="h-24 w-24 rounded-full border border-neutral-200 object-cover shadow-sm"
                  width={96}
                  height={96}
                />
                <p className="mt-5 text-sm font-medium text-neutral-900">Nathan McCarley</p>
                <p className={sectionLeadClass}>Founder &amp; CEO</p>

                <div className="mt-6 w-full border-t border-neutral-100 pt-6">
                  <img
                    src="/images/UNC_logo.jpg"
                    alt=""
                    className="mx-auto h-11 w-11 object-contain"
                    width={44}
                    height={44}
                    aria-hidden
                  />
                  <p className="mx-auto mt-4 max-w-xs text-sm font-medium leading-snug text-neutral-800">
                    The University of North Carolina at Chapel Hill
                  </p>
                  <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-neutral-600">
                    B.A. Physics, Quantitative Finance Concentration · Kenan-Flagler Business School
                  </p>
                  <p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-neutral-600">
                    Applied Mathematics Minor
                  </p>
                </div>
                <p className="mt-6">
                  <a href={`mailto:${EMAIL}`} className={`text-sm ${linkClass}`}>
                    {EMAIL}
                  </a>
                </p>
                <p className="mt-2">
                  <a
                    href={LINKEDIN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm ${linkClass}`}
                  >
                    LinkedIn
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-neutral-200 py-6">
        <nav className={`${wideContainer} flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-center text-xs text-neutral-500`}>
          <Link to="/support" className="hover:text-neutral-900">
            Support
          </Link>
          <span className="text-neutral-300" aria-hidden>
            |
          </span>
          <Link to="/disclaimer" className="hover:text-neutral-900">
            Disclaimer
          </Link>
          <span className="text-neutral-300" aria-hidden>
            |
          </span>
          <Link to="/privacy-policy" className="hover:text-neutral-900">
            Privacy Policy
          </Link>
          <span className="text-neutral-300" aria-hidden>
            |
          </span>
          <Link to="/terms-and-conditions" className="hover:text-neutral-900">
            Terms &amp; Conditions
          </Link>
        </nav>
        <p className={`${wideContainer} mt-4 text-center text-xs text-neutral-500`}>
          © 2026 LandSentry LLC. LandSentry™ is a trademark of LandSentry LLC. All rights reserved.
        </p>
      </footer>
    </div>
  )
}
